import React, { useEffect, useState } from 'react';
import { useNotifications, Notification } from '../contexts/NotificationContext';
import { useLocation } from 'wouter';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { EnhancedHeader } from '@/components/ui/enhanced-header';

dayjs.extend(relativeTime);

const NotificationsPage: React.FC = () => {
  const { 
    notifications, 
    unreadCount,
    isLoading, 
    error, 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useNotifications();
  const [_, navigate] = useLocation();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  
  useEffect(() => {
    document.title = 'Notifications';
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.isRead) {
      markAsRead([notification.id]);
    }
    
    // Navigate to action URL if provided
    if (notification.actionUrl) {
      // Handle special cases
      if (notification.type.includes('friend_request') && notification.actionUrl === '/friends') {
        navigate('/messages');
      } else {
        navigate(notification.actionUrl);
      }
    }
  };
  
  // Filter notifications based on selected filter
  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications.filter(notification => !notification.isRead);
  
  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce<Record<string, Notification[]>>((groups, notification) => {
    const date = dayjs(notification.createdAt).format('YYYY-MM-DD');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(notification);
    return groups;
  }, {});
  
  // Sort dates newest first
  const sortedDates = Object.keys(groupedNotifications).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );
  
  // Get formatted date heading
  const getDateHeading = (dateStr: string) => {
    const date = dayjs(dateStr);
    const today = dayjs().startOf('day');
    const yesterday = dayjs().subtract(1, 'day').startOf('day');
    
    if (date.isSame(today, 'day')) {
      return 'Today';
    } else if (date.isSame(yesterday, 'day')) {
      return 'Yesterday';
    } else {
      return date.format('MMMM D, YYYY');
    }
  };
  
  // Function to get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'friend_request':
      case 'friend_request_accepted':
        return (
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        );
      case 'game_invite':
      case 'game_turn':
        return (
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'game_completed':
      case 'round_completed':
        return (
          <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'achievement':
        return (
          <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getActionText = (type: string) => {
    if (type.includes('game')) return 'View Game';
    if (type.includes('friend')) return 'View Friends';
    if (type.includes('achievement')) return 'View Achievements';
    return 'View Details';
  };

  return (
    <div>
       <EnhancedHeader
          title="Notifications"
          backPath="/"
          coloredText={true}
          rightContent={
            <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <button 
              onClick={() => setFilter('all')} 
              className={`px-4 py-2 text-sm rounded-l-md ${filter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
            >
              All
            </button>
            <button 
              onClick={() => setFilter('unread')} 
              className={`px-4 py-2 text-sm rounded-r-md ${filter === 'unread' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
            >
              Unread {unreadCount > 0 && `(${unreadCount})`}
            </button>
          </div>
          
          {unreadCount > 0 && (
            <button 
              onClick={() => markAllAsRead()}
              className="text-sm text-primary hover:text-primary/90"
            >
              Mark all as read
            </button>
          )}
        </div>
          }
          showBackButton={true}
        />


<div className="container mx-auto p-4 max-w-4xl">
      
      
      {isLoading ? (
        <div className="p-8 text-center">
          <svg className="animate-spin h-8 w-8 mx-auto text-muted-foreground" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4">Loading notifications...</p>
        </div>
      ) : error ? (
        <div className="p-8 text-center text-destructive bg-destructive/10 rounded-md">
          <p>{error}</p>
          <button 
            onClick={() => fetchNotifications()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md"
          >
            Try Again
          </button>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="p-16 text-center bg-secondary/50 rounded-md">
          <svg className="h-16 w-16 mx-auto mb-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <p className="text-muted-foreground text-lg">
            {filter === 'all' ? 'No notifications yet' : 'No unread notifications'}
          </p>
          {filter === 'unread' && notifications.length > 0 && (
            <button 
              onClick={() => setFilter('all')}
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md"
            >
              Show all notifications
            </button>
          )}
        </div>
      ) : (
        <div>
          {sortedDates.map(date => (
            <div key={date} className="mb-8">
              <h2 className="text-lg font-medium mb-4 text-muted-foreground">{getDateHeading(date)}</h2>
              <div className="bg-card rounded-lg shadow border border-border">
                {groupedNotifications[date].map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`p-4 border-b border-border last:border-b-0 hover:bg-accent transition-colors duration-150 relative ${!notification.isRead ? 'bg-accent/25' : ''}`}
                  >
                    <button 
                      className="flex w-full text-left focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-sm"
                      onClick={() => handleNotificationClick(notification)}
                      aria-label={`${notification.title}: ${notification.message}`}
                    >
                      <div className="flex-shrink-0 mr-4">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-grow pr-12">
                        <div className="flex justify-between">
                          <h3 className="font-medium">{notification.title}</h3>
                          <span className="text-sm text-muted-foreground">
                            {dayjs(notification.createdAt).format('h:mm A')}
                          </span>
                        </div>
                        <p className="text-muted-foreground mt-1">{notification.message}</p>
                        {notification.actionUrl && (
                          <div className="mt-2">
                            <span className="text-sm text-primary">
                              {getActionText(notification.type)}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                    <div className="absolute top-4 right-4 flex space-x-2">
                      {!notification.isRead && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead([notification.id]);
                          }}
                          className="text-primary hover:text-primary/80"
                          title="Mark as read"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      )}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete notification"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    </div>
    
  );
};

export default NotificationsPage;